// Shared utility for consistent image URL handling across all components

// Helper function to normalize image URLs with versioning and CDN support
export const withVersion = (url, version) => {
  if (!url) return "";
  
  try {
    // If it's already an absolute URL, don't prepend base URL
    const u = url.startsWith('http') ? new URL(url) : new URL(url, typeof window !== "undefined" ? window.location.origin : "https://zerogiveup.com");
    if (version != null) u.searchParams.set("v", String(version));
    return u.toString();
  } catch {
    // relative path fallback with CDN support
    return version != null ? `${url}${url.includes("?") ? "&" : "?"}v=${version}` : url;
  }
};

// Helper function to normalize image URLs with consistent cache-busting
// Handles both S3 URLs and legacy local URLs
export const getImageUrl = (key, cacheBust = false, version = null) => {
  if (!key) return null;
  
  // If it's already a full URL (S3 or any other), return as-is without modification
  if (key.startsWith('https://') || key.startsWith('http://')) {
    return key;
  }
  
  // Strip any leading // 
  if (key.startsWith('//')) {
    key = key.substring(2);
  }
  
  // Legacy support: Convert old local paths to relative URLs for fallback
  // (These should be phased out as all new uploads go to S3)
  if (key.startsWith('/api/uploads/')) {
    return version ? withVersion(key, version) : (cacheBust ? `${key}?v=${Date.now()}` : key);
  }
  
  // Legacy: If it starts with /api/files/, convert to /api/uploads/
  if (key.startsWith('/api/files/')) {
    const url = `/api/uploads/${key.substring(11)}`;
    return version ? withVersion(url, version) : (cacheBust ? `${url}?v=${Date.now()}` : url);
  }
  
  // Legacy: If it starts with /uploads/, convert to /api/uploads/
  if (key.startsWith('/uploads/')) {
    const url = `/api/uploads/${key.substring(9)}`;
    return version ? withVersion(url, version) : (cacheBust ? `${url}?v=${Date.now()}` : url);
  }
  
  // If it has no path prefix, treat it as a key and add /api/uploads/ (for legacy compatibility)
  const url = `/api/uploads/${key}`;
  return version ? withVersion(url, version) : (cacheBust ? `${url}?v=${Date.now()}` : url);
};

// Get avatar URL with proper versioning based on user's updated_at timestamp
export const getAvatarUrl = (user) => {
  const src = user?.profile_photo || "/images/avatar-fallback.png";
  const version = user?.updated_at ? new Date(user.updated_at).getTime() : Date.now();
  return getImageUrl(src, false, version);
};

// Profile images should use consistent URLs across environments with user-based versioning
export const getProfileImageUrl = (key, user = null) => {
  if (user) {
    return getAvatarUrl(user);
  }
  // Fallback to old behavior for compatibility
  return getImageUrl(key, true);
};

// Get post image URL with proper versioning based on post's updated_at timestamp
export const getPostImageUrl = (post) => {
  const src = post?.image_url || post?.imageUrl || (post?.imageKey ? `/uploads/${post.imageKey}` : "");
  if (!src) return "";
  
  // For S3 URLs, don't add versioning as it can break the signed URL
  if (src.startsWith('https://') || src.startsWith('http://')) {
    return src;
  }
  
  const version = post?.updated_at ? new Date(post.updated_at).getTime() : null;
  return getImageUrl(src, false, version);
};

// Get blog image URL with proper versioning based on post's updated_at timestamp
export const getBlogImageUrl = (post) => {
  const src = post?.image_url || post?.featured_image_url || "";
  if (!src) return "";
  
  // For S3 URLs, don't add versioning as it can break the signed URL
  if (src.startsWith('https://') || src.startsWith('http://')) {
    return src;
  }
  
  const version = post?.updated_at ? new Date(post.updated_at).getTime() : null;
  return getImageUrl(src, false, version);
};